import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SearchBox from "@/components/feature/SearchBox";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar, { type FilterState } from "@/components/feature/FilterBar";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import { filterMovies, getPopularMovies, searchMovies } from "@/lib/catalog";

const DEFAULT_FILTER: FilterState = {
  genres: [],
  year: "all",
  minRating: 0,
  sort: "latest",
};

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q")?.trim() ?? "";

  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);

  useEffect(() => {
    setFilter(DEFAULT_FILTER);
  }, [keyword]);

  const matched = useMemo(() => searchMovies(keyword), [keyword]);
  const results = useMemo(() => filterMovies(matched, filter), [matched, filter]);
  const recommended = getPopularMovies(4);

  useSeo({
    title: keyword
      ? `“${keyword}”的搜索结果 - 年轻的母亲在线`
      : "搜索影视 - 年轻的母亲在线",
    description: keyword
      ? `在年轻的母亲在线搜索“${keyword}”，共找到 ${results.length} 部相关影视资源，查看剧情介绍与详情。`
      : "在年轻的母亲在线搜索电影、电视剧名称，快速查找相关影视资源与剧情介绍。",
    keywords: `${keyword || "影视搜索"},年轻的母亲在线,影视搜索,在线观看,剧情介绍`,
  });

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]}
      />

      <header className="mt-5 mb-6">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          搜索影视
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入电影、电视剧名称或类型关键词，快速查找你想看的内容。
        </p>
        <div className="mt-5 max-w-2xl">
          <SearchBox defaultValue={keyword} />
        </div>
      </header>

      {keyword ? (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-foreground-500">搜索关键词：</span>
            <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
              {keyword}
            </span>
            <span className="text-foreground-500">
              共找到 <strong className="text-primary-600">{results.length}</strong> 部相关影视
            </span>
          </div>

          {matched.length > 0 && (
            <div className="mb-6">
              <FilterBar
                value={filter}
                onChange={setFilter}
                onReset={() => setFilter(DEFAULT_FILTER)}
                resultCount={results.length}
              />
            </div>
          )}

          {results.length === 0 ? (
            <>
              <EmptyState
                title="没有找到相关影视"
                description={`暂时没有与“${keyword}”匹配的影视资源，换个关键词试试，或看看下面这些热门推荐。`}
                actionLabel="返回首页"
                actionTo="/"
              />
              <section className="mt-10">
                <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4">
                  热门推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6">
                  {recommended.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </section>
            </>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6">
              {results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}
        </>
      ) : (
        <div>
          <EmptyState
            title="输入关键词开始搜索"
            description="例如“年轻的母亲”“家庭”“悬疑”等，即可快速找到相关影视资源。"
          />
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4">
              大家都在看
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6">
              {recommended.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 rounded-lg bg-background-100 border border-background-200 p-4">
        <h2 className="text-sm font-medium text-foreground-800 mb-2.5">
          热门搜索
        </h2>
        <div className="flex flex-wrap gap-2">
          {["年轻的母亲", "家庭", "悬疑", "都市", "治愈", "古装"].map((tag) => (
            <Link
              key={tag}
              to={`/search?q=${encodeURIComponent(tag)}`}
              className="px-3 py-1 text-xs rounded-full bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
            >
              {tag}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}